import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Text,
  Tooltip,
  Link,
  useColorModeValue,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverBody,
  Button, // For PopoverTrigger if needed, or use asChild
} from '@chakra-ui/react';
import { processCitations as utilityProcessCitations } from '../../utils/citationUtils'; // Renamed to avoid conflict

/**
 * Component that renders text with hyperlinks to sources.
 * It now primarily uses a 'references' prop which is expected to be an array of structured reference objects.
 * It can still optionally use 'groundingMetadata' for AI-grounded links if provided.
 */
const SourceLinkedText = ({ text: textProp, groundingMetadata: explicitGroundingMetadataProp, references: referencesProp = [], showCitations }) => {
  // Use semantic colors from our theme
  const superscriptColor = useColorModeValue('brand.primary', 'brand.accent');

  // The 'referencesProp' is now the primary source of citation data.
  // It's assumed to be already processed and structured correctly by parent components or services.
  const processedReferences = useMemo(() => {
    // If referencesProp needs processing (e.g. from an older format), do it here.
    // For now, assume it's correctly formatted as per the new `references` array structure.
    return referencesProp;
  }, [referencesProp]);

  // Extract value if textProp is an object with value property, otherwise use textProp directly
  const actualText = (typeof textProp === 'object' && textProp !== null && 'value' in textProp)
    ? textProp.value
    : textProp;

  let finalGroundingMetadata = explicitGroundingMetadataProp;
  if (!finalGroundingMetadata && typeof textProp === 'object' && textProp !== null && 'groundingMetadata' in textProp) {
    finalGroundingMetadata = textProp.groundingMetadata;
  }

  // Ensure actualText is a string before proceeding.
  if (typeof actualText !== 'string') {
    console.warn('SourceLinkedText: Expected text to be a string or an object with a value property, but received:', textProp);
    return <Text>{''}</Text>; // Render empty string if actualText is not a string
  }
  
  // Check if we have grounding supports (linking info to sources) using finalGroundingMetadata
  const hasGroundingSupports = finalGroundingMetadata &&
                               Array.isArray(finalGroundingMetadata.groundingSupports) &&
                               finalGroundingMetadata.groundingSupports.length > 0;

  // Helper function to process text segments for in-text citation markers like [1], [2]
  // This function now uses `processedReferences`.
  const processInTextMarkers = (textSegment, keyPrefix) => {
    const MARKER_REGEX = /\[(\d+)\]/g; // Matches [1], [2], etc.
    
    if (!textSegment) return [];
    
    const result = [];
    let lastIndex = 0;
    let match;
    
    const regex = new RegExp(MARKER_REGEX);
    
    while ((match = regex.exec(textSegment)) !== null) {
      const markerText = match[0]; // e.g., "[1]"
      const citationNumberStr = match[1]; // e.g., "1"
      const startIndex = match.index;
      const endIndex = startIndex + markerText.length;
      
      if (startIndex > lastIndex) {
        result.push(textSegment.substring(lastIndex, startIndex));
      }
      
      // Find the corresponding reference by its 'id' field (which should be like "[1]")
      // or by matching the number if the id format is different.
      let reference = processedReferences.find(ref => ref.id === markerText || (ref.number && String(ref.number) === citationNumberStr));
      if (!reference) {
        const citationIndex = parseInt(citationNumberStr, 10) - 1;
        if (citationIndex >= 0 && citationIndex < processedReferences.length) {
          reference = processedReferences[citationIndex];
        }
      }
      
      if (reference) {
        if (showCitations) {
          const renderTooltipContent = (refData) => (
          <Box
            p={3}
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.default"
            bg="bg.tooltip"
            boxShadow="md"
            maxW="300px"
          >
            <Text
              fontWeight="medium"
              mb={2}
              fontFamily="heading"
              borderBottomWidth="1px"
              borderColor="border.light"
              pb={1}
            >
              {refData.title || 'Source'}
            </Text>
            <Text
              fontSize="sm"
              fontFamily="body"
              color="text.muted"
            >
              {refData.referenceText || refData.description || 'No further details available.'}
            </Text>
          </Box>
        );
        
        const getSafeHtmlId = (referenceId) => {
          if (!referenceId) return '';
          return `ref-${referenceId.replace(/[\[\]\s#.]/g, '')}`;
        };

        const isExternalLink = reference.url && reference.url.startsWith('http');
        // For internal links, construct href to target the safe HTML ID
        const targetUrl = isExternalLink ? reference.url : (reference.id ? `#${getSafeHtmlId(reference.id)}` : '#');

        // Display text: use title if available, otherwise URL, otherwise marker
        const displayText = reference.title || (reference.url && reference.url.length > 40 ? reference.url.substring(0, 40) + '...' : reference.url) || markerText;
        
        const linkElement = (
          <Tooltip
            key={`${keyPrefix}-ref-${citationNumberStr}`}
            label={renderTooltipContent(reference)}
            placement="top"
            hasArrow
            interactive // Make tooltip interactive
          >
            { (reference.url || reference.id) ? ( // Make it a link if there's a URL or an ID to scroll to
              <Link
                href={targetUrl}
                isExternal={isExternalLink}
                onClick={(e) => {
                  if (!isExternalLink && reference.id) {
                    e.preventDefault();
                    const safeId = getSafeHtmlId(reference.id);
                    const targetElement = document.getElementById(safeId);
                    if (targetElement) {
                      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    } else {
                      console.warn(`Target element with ID '${safeId}' not found for reference:`, reference.id);
                    }
                  }
                }}
                display="inline"
                borderRadius="sm"
                px={1}
                color={superscriptColor}
                textDecoration="underline"
                _hover={{
                  textDecoration: 'underline',
                  backgroundColor: 'rgba(100, 100, 255, 0.1)'
                }}
              >
                {displayText}
              </Link>
            ) : ( // No valid URL, just a span with tooltip
              <Box
                as="span"
                display="inline"
                borderRadius="sm"
                px={1}
                color={superscriptColor}
                textDecoration="underline"
                _hover={{
                  backgroundColor: 'rgba(100, 100, 255, 0.1)'
                }}
                cursor="pointer"
              >
                {displayText}
              </Box>
            )}
          </Tooltip>
        );
          result.push(linkElement);
        }
        // If !showCitations, a valid marker is intentionally hidden.
      } else {
        // No valid reference found for this marker.
        if (showCitations) {
          result.push(
            <Box
              as="span"
              key={`${keyPrefix}-no-ref-${citationNumberStr}`}
              display="inline"
              color={superscriptColor}
              fontWeight="bold"
              fontSize="xs"
              title="Reference not found"
            >
              {markerText}
            </Box>
          );
        } else {
          // Not a valid reference, and markers are hidden. Render as plain text.
          result.push(markerText);
        }
      }
      lastIndex = endIndex;
    }
    
    if (lastIndex < textSegment.length) {
      result.push(textSegment.substring(lastIndex));
    }
    
    return result;
  };
  
  const segmentsWithLinks = useMemo(() => {
    const elements = [];
    let lastIndex = 0;

    // If AI grounding supports are present, process them first.
    // Otherwise, or for segments not covered by AI grounding, use processInTextMarkers.
    if (hasGroundingSupports) {
      const processedSupports = (finalGroundingMetadata.groundingSupports || [])
        .map((support, originalIndex) => {
          if (!support || !support.segment || !support.segment.text || !support.groundingChunkIndices || support.groundingChunkIndices.length === 0) {
            if (process.env.NODE_ENV === 'development') {
              console.warn(`SourceLinkedText: Invalid AI support structure (original index ${originalIndex}). Skipping.`, support);
            }
            return null;
          }
          const sourceDetails = support.groundingChunkIndices
            .map(idx => {
              const chunk = finalGroundingMetadata.groundingChunks && finalGroundingMetadata.groundingChunks[idx];
              return { source: chunk, originalIndex: idx }; // Keep originalIndex from groundingChunks for tooltip
            })
            .filter(detail => detail.source && detail.source.web && detail.source.web.uri && detail.source.web.title);

          if (sourceDetails.length === 0) {
            if (process.env.NODE_ENV === 'development') {
              console.warn(`SourceLinkedText: AI Support (original index ${originalIndex}) for segment "${support.segment.text}" has no valid sourceDetails. This segment will not be linked.`, support);
            }
            return null;
          }
          return {
            segmentText: support.segment.text,
            sourceDetails, // Array of { source: chunkData, originalIndex: chunkIndex }
            originalSupportIndex: originalIndex, // Index of this support object in groundingSupports
          };
        })
        .filter(Boolean);

      processedSupports.sort((a, b) => {
        const posA = actualText.indexOf(a.segmentText);
        const posB = actualText.indexOf(b.segmentText);
        if (posA === -1 && posB === -1) return a.originalSupportIndex - b.originalSupportIndex;
        if (posA === -1) return 1;
        if (posB === -1) return -1;
        if (posA !== posB) return posA - posB;
        const lenDiff = b.segmentText.length - a.segmentText.length;
        if (lenDiff !== 0) return lenDiff;
        return a.originalSupportIndex - b.originalSupportIndex;
      });

      processedSupports.forEach((procSupport, procSupportGlobalIndex) => {
        const segmentText = procSupport.segmentText;
        const lowerActualText = actualText.toLowerCase();
        const lowerSegmentText = segmentText.toLowerCase();
        
        const startIndex = lowerActualText.indexOf(lowerSegmentText, lastIndex);

        if (startIndex !== -1) {
          if (startIndex > lastIndex) {
            const ungroundedText = actualText.substring(lastIndex, startIndex);
            elements.push(...processInTextMarkers(ungroundedText, `ungrounded-before-${procSupportGlobalIndex}`));
          }
          // This is an AI-grounded segment. It might also contain in-text markers like [1].
          // So, we process its content with processInTextMarkers as well.
          const groundedSegmentContent = actualText.substring(startIndex, startIndex + segmentText.length);
          const processedGroundedContent = processInTextMarkers(groundedSegmentContent, `grounded-segment-${procSupportGlobalIndex}`);
          
          elements.push({
            type: 'ai_grounded_link_wrapper', // Wrapper for AI-grounded segment
            contentElements: processedGroundedContent, // Array of text and/or marker elements
            sourceDetails: procSupport.sourceDetails, // For the AI grounding tooltip
          });
          lastIndex = startIndex + segmentText.length;
        }
      });
    }

    // Process any remaining text after the last AI-grounded segment, or the whole text if no AI grounding.
    if (lastIndex < actualText.length) {
      const remainingText = actualText.substring(lastIndex);
      elements.push(...processInTextMarkers(remainingText, 'remaining-text'));
    }
    
    // If elements is empty (e.g. actualText was empty, or AI grounding produced nothing and remaining text was empty)
    // and we didn't attempt AI grounding, ensure we process the whole text.
    if (elements.length === 0 && !hasGroundingSupports) {
        return processInTextMarkers(actualText, 'full-text-fallback');
    }

    return elements;
  }, [actualText, finalGroundingMetadata, processedReferences, hasGroundingSupports, showCitations]);
  
  // Tooltip for AI-grounded citations (remains largely the same, uses finalGroundingMetadata)
  const renderAIGroundedTooltipContent = (allSourceDetailsForSegment, highlightedOriginalIndex) => {
    // These colors are relative to the Tooltip's own background
    const mutedTextColorInTooltip = useColorModeValue('whiteAlpha.700', 'blackAlpha.700');
    const highlightedColorInTooltip = useColorModeValue('white', 'black'); // Main color for highlighted

    return (
      <Box
        key={`tooltip-content-for-${highlightedOriginalIndex}`}
        p={3}
        borderRadius="md"
        maxW="400px"
      >
        <Text
          fontWeight="semibold"
          mb={2}
          fontFamily="heading"
          pb={1.5}
          borderBottomWidth="1px"
          borderColor={useColorModeValue('whiteAlpha.300', 'blackAlpha.300')}
        >
          AI Grounded Source
        </Text>
        {allSourceDetailsForSegment.map(({ source, originalIndex: currentChunkIndex }, i) => {
          const isHighlighted = currentChunkIndex === highlightedOriginalIndex;
          // AI Grounding specific: citationMetadata.citationSources is an array, originalIndex refers to this.
          const citationMetaForChunk = finalGroundingMetadata?.citationMetadata?.citationSources?.[currentChunkIndex];
          const retrievedExcerpt = citationMetaForChunk?.retrievedInfo?.[0] || citationMetaForChunk?.retrieved_info?.[0];

          return (
            <Box key={i} mt={isHighlighted ? 2 : 1} py={isHighlighted ? 1.5 : 0.5}>
              <Text
                fontSize="sm"
                fontFamily="body"
                color={isHighlighted ? highlightedColorInTooltip : mutedTextColorInTooltip}
                fontWeight={isHighlighted ? 'bold' : 'normal'}
              >
                {/* For AI grounding, the number is chunkIndex + 1 */}
                {`[AI-${currentChunkIndex + 1}] `}{source.web.title || 'Untitled Source'}
              </Text>
              {isHighlighted && retrievedExcerpt && ( // Excerpt shown only for highlighted (main) source
                <Text fontSize="xs" color={highlightedColorInTooltip} mt={1} fontStyle="italic" noOfLines={3}>
                  Excerpt: "{retrievedExcerpt}"
                </Text>
              )}
              {/* "View source" link should appear for ALL sources with a URI, not just the highlighted one */}
              {!retrievedExcerpt && source.web.uri && (
                 <Link 
                   href={source.web.uri} 
                   isExternal 
                   fontSize="xs" 
                   color={isHighlighted ? highlightedColorInTooltip : mutedTextColorInTooltip} // Adjust color based on highlight
                   opacity={0.8} 
                   mt={1}
                   _hover={{ opacity: 1, textDecoration: 'underline' }} // Ensure hover works
                 >
                   View source
                 </Link>
               )}
            </Box>
          );
        })}
      </Box>
    );
  };
  
  const tooltipBg = useColorModeValue('gray.700', 'gray.200');
  const tooltipColor = useColorModeValue('whiteAlpha.900', 'blackAlpha.900');

  return (
    <Text as="span">
      {segmentsWithLinks.map((segment, index) => {
        const key = `segment-${index}`;

        if (typeof segment === 'string') {
          return <React.Fragment key={key}>{segment}</React.Fragment>;
        }
        
        if (React.isValidElement(segment)) { // This handles elements from processInTextMarkers
          return segment; 
        }

        // Handle AI-grounded link wrappers
        if (segment && typeof segment === 'object' && segment.type === 'ai_grounded_link_wrapper') {
          // The contentElements are already processed text and marker elements.
          const hasMultipleSources = segment.sourceDetails && segment.sourceDetails.length > 1;
          const singleSourceDetail = segment.sourceDetails && segment.sourceDetails.length === 1 ? segment.sourceDetails[0] : null;
          const primaryUrl = singleSourceDetail?.source?.web?.uri; // URL only if it's a single source

          const contentElementsRendered = segment.contentElements.map((el, i) => <React.Fragment key={`${key}-el-${i}`}>{el}</React.Fragment>);

          if (showCitations) {
            // Citations are ON
            const tooltipLabel = renderAIGroundedTooltipContent(segment.sourceDetails, singleSourceDetail ? singleSourceDetail.originalIndex : (segment.sourceDetails[0] ? segment.sourceDetails[0].originalIndex : -1) );
            
            // Base style for the text, underline if it will be a link or if multiple sources (to indicate rich tooltip)
            const textDecorationStyle = (primaryUrl || hasMultipleSources) ? "underline" : "none";

            const textSpanContent = (
              <Text 
                as="span" 
                display="inline" 
                _hover={{ backgroundColor: 'rgba(0, 255, 0, 0.05)' }} 
                textDecoration={textDecorationStyle} 
                textDecorationColor="rgba(0, 255, 0, 0.3)"
                cursor={primaryUrl || hasMultipleSources ? "pointer" : "default"} // Cursor pointer if interactive
              >
                {contentElementsRendered}
              </Text>
            );

            if (primaryUrl && !hasMultipleSources) { // It's a link only if single source with URL
              return (
                <Tooltip
                  key={`${key}-ai-tooltip-singlelink`}
                  label={tooltipLabel}
                  placement="top"
                  hasArrow
                  interactive
                  bg={tooltipBg}
                  color={tooltipColor}
                >
                  <Link href={primaryUrl} isExternal>
                    {textSpanContent}
                  </Link>
                </Tooltip>
              );
            } else { // Multiple sources OR single source without URL: Use Popover for click interaction
              return (
                <Popover placement="top" closeOnBlur={true} >
                  <PopoverTrigger>
                    {/* This is the textSpanContent, acting as the trigger */}
                    {/* We might need to wrap it in a Button or ensure it's a valid trigger */}
                    <Box as="span" display="inline" cursor="pointer"> 
                      {textSpanContent}
                    </Box>
                  </PopoverTrigger>
                  <PopoverContent bg={tooltipBg} color={tooltipColor} borderColor="border.default" boxShadow="lg">
                    <PopoverArrow bg={tooltipBg} />
                    <PopoverCloseButton />
                    <PopoverBody p={0}> {/* renderAIGroundedTooltipContent already has padding */}
                      {tooltipLabel}
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              );
            }
          } else {
            // Citations are OFF: render plain text content
            return (
              <Text as="span" display="inline" key={`${key}-ai-plain`}>
                {contentElementsRendered}
              </Text>
            );
          }
        }
        
        // Fallback for old 'link' type (should be phased out or handled by ai_grounded_link_wrapper)
        if (segment && typeof segment === 'object' && segment.type === 'link') { 
          // This was the old structure for AI-grounded links.
          // It should ideally be handled by the 'ai_grounded_link_wrapper' now.
          // For backward compatibility or if some paths still produce this:
          const contentWithMarkers = processInTextMarkers(segment.content, `old-link-content-${index}`);
          const firstSourceDetail = segment.sourceDetails[0];
          if (!firstSourceDetail || !firstSourceDetail.source?.web?.uri) {
            return contentWithMarkers.map((el, i) => <React.Fragment key={`${key}-old-link-el-${i}`}>{el}</React.Fragment>);
          }
          return (
            <Tooltip
              key={`${key}-old-link-tooltip`}
              label={renderAIGroundedTooltipContent(segment.sourceDetails, firstSourceDetail.originalIndex)}
              placement="top"
              hasArrow
              bg={tooltipBg}
              color={tooltipColor}
            >
              <Text as="span" display="inline" _hover={{ backgroundColor: 'rgba(0, 255, 0, 0.05)' }}>
                {contentWithMarkers.map((el, i) => <React.Fragment key={`${key}-old-link-el-${i}`}>{el}</React.Fragment>)}
              </Text>
            </Tooltip>
          );
        }
        
        if (segment && typeof segment === 'object' && segment.type) {
           console.warn(`SourceLinkedText: Unhandled object segment type "${segment.type}" in rendering map. Content:`, segment.content || segment.contentElements);
        } else if (segment) {
           console.warn("SourceLinkedText: Unexpected segment data in rendering map:", segment);
        }
        return null; 
      })}
    </Text>
  );
};

SourceLinkedText.propTypes = {
  text: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      groundingMetadata: PropTypes.object // Keep if AI grounding is still a feature
    })
  ]),
  groundingMetadata: PropTypes.shape({ // For AI-grounded links, if still used
    groundingChunks: PropTypes.arrayOf(
      PropTypes.shape({
        web: PropTypes.shape({
          uri: PropTypes.string,
          title: PropTypes.string
        })
      })
    ),
    groundingSupports: PropTypes.arrayOf(
      PropTypes.shape({
        segment: PropTypes.shape({
          text: PropTypes.string
        }),
        groundingChunkIndices: PropTypes.arrayOf(PropTypes.number)
      })
    ),
    citationMetadata: PropTypes.object
  }),
  references: PropTypes.arrayOf(PropTypes.shape({ // New primary prop for citations
    id: PropTypes.string.isRequired, // e.g., "[1]"
    title: PropTypes.string,
    url: PropTypes.string,
    type: PropTypes.string, // 'web', 'book', 'journal', 'wiki', 'document', 'unknown'
    description: PropTypes.string,
    referenceText: PropTypes.string, // Full reference text
    retrievedInfo: PropTypes.arrayOf(PropTypes.string), // Sentences attributed to this source
    publicationDate: PropTypes.string,
    license: PropTypes.string,
  })),
  showCitations: PropTypes.bool,
};

SourceLinkedText.defaultProps = {
  references: [], // Default to empty array for the new primary prop
  showCitations: true,
};

export default SourceLinkedText;
